import React, { Fragment, useMemo } from "react";

import clsx from "clsx";

import { useSalesBreakdownConfigs } from "@components/data/sales-breakdown-configs";
import {
  Popover,
  PopoverButton,
  PopoverPanel,
  Transition,
} from "@headlessui/react";
import { type CategoricalBreakdownConfig } from "@north-beam/nb-common";

import { type TopLevelBreakdown } from "../../types";

import { BreakdownValueSelectorContent } from "./breakdown-value-selector-content";

interface Props {
  breakdown: TopLevelBreakdown;
  onUpdate(values: string[]): void;
  onDelete(): void;
}

export const FilterButtonActive = ({
  breakdown,
  onUpdate,
  onDelete,
}: Props) => {
  const { value: breakdownConfigs } = useSalesBreakdownConfigs();
  const breakdownConfig = useMemo(
    () =>
      breakdownConfigs.find((e) => e.key === breakdown.key) as
        | CategoricalBreakdownConfig
        | undefined,
    [breakdown, breakdownConfigs],
  );

  if (!breakdownConfig) {
    return null;
  }

  return (
    <Popover className="relative">
      {({ open, close }) => (
        <>
          <PopoverButton
            as="button"
            className={clsx(
              "button sm secondary-button h-7 py-0 max-w-[106px] truncate",
              {
                "bg-cosmic-4 border-cosmic-4 hover:bg-cosmic-3 hover:border-cosmic-3":
                  !open,
                "bg-cosmic border-cosmic text-white": open,
              },
            )}
          >
            ({breakdown.values.length}) {breakdownConfig.name}
          </PopoverButton>
          <Transition
            as={Fragment}
            enter="transition ease-out duration-200"
            enterFrom="opacity-0 translate-y-1"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-1"
          >
            <PopoverPanel
              className={clsx(
                "absolute right-0 z-10 bg-white font-sans rounded-lg elevation-1 text-sm",
                "pt-3 w-[250px]",
              )}
            >
              <BreakdownValueSelectorContent
                breakdown={breakdown}
                onUpdate={(values) => {
                  onUpdate(values);
                  close();
                }}
                onDelete={() => {
                  onDelete();
                  close();
                }}
              />
            </PopoverPanel>
          </Transition>
        </>
      )}
    </Popover>
  );
};
